import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_products: 0,
    total_skus: 0,
    total_brands: 0,
    total_vendors: 0,
    total_warehouses: 0,
    low_stock_alerts: 0,
    total_stock_value: 0,
    top_brands: [],
    recent_activities: []
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, lowStockRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/products/low-stock'),
      ]);
      setStats(statsRes.data);
      setLowStockItems(lowStockRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const statsCards = [
    {
      title: 'Total Products',
      value: stats.total_products,
      icon: '👗',
      color: 'stats-card-blue',
      description: 'Active designs'
    },
    {
      title: 'Total SKUs',
      value: stats.total_skus,
      icon: '📦',
      color: 'stats-card-emerald',
      description: 'Size + Color variants'
    },
    {
      title: 'Brands',
      value: stats.total_brands,
      icon: '🏷️',
      color: 'stats-card-amber',
      description: 'Active brands'
    },
    {
      title: 'Stock Alerts',
      value: stats.low_stock_alerts,
      icon: '⚠️',
      color: 'stats-card-red',
      description: 'Low stock items'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="dashboard-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            {getGreeting()}, {user?.full_name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-gray-600 mt-2 text-lg">Welcome to your Garment360 ERP dashboard</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Current Role</div>
          <Badge className="bg-blue-100 text-blue-800 px-3 py-1 text-sm font-medium capitalize">
            {user?.role?.replace('_', ' ')}
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {statsCards.map((stat, index) => (
          <Card key={index} className={`${stat.color} border-0 relative overflow-hidden`} data-testid={`${stat.title.toLowerCase().replace(/\\s+/g, '-')}-card`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardDescription className="font-medium text-gray-700">{stat.title}</CardDescription>
                  <CardTitle className="text-3xl font-bold text-gray-900 mt-1">
                    {stat.value.toLocaleString()}
                  </CardTitle>
                </div>
                <div className="text-3xl opacity-80">
                  {stat.icon}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-600">
                {stat.description}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Low Stock Alerts Panel */}
      {lowStockItems.length > 0 && (
        <Card className="border-red-200 bg-red-50/30" data-testid="low-stock-alerts-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                Low Stock Alerts ({lowStockItems.length})
              </CardTitle>
              <button onClick={() => setShowAllAlerts(!showAllAlerts)} className="text-sm text-red-600 hover:text-red-800 font-medium" data-testid="toggle-low-stock-alerts">
                {showAllAlerts ? 'Show Less' : 'View All'}
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-red-200">
                    <th className="text-left py-2 px-3 font-semibold text-red-800">Design</th>
                    <th className="text-left py-2 px-3 font-semibold text-red-800">Brand</th>
                    <th className="text-left py-2 px-3 font-semibold text-red-800">Size</th>
                    <th className="text-left py-2 px-3 font-semibold text-red-800">Color</th>
                    <th className="text-right py-2 px-3 font-semibold text-red-800">Stock</th>
                    <th className="text-right py-2 px-3 font-semibold text-red-800">Min Level</th>
                    <th className="text-right py-2 px-3 font-semibold text-red-800">Deficit</th>
                    <th className="text-left py-2 px-3 font-semibold text-red-800">Warehouse</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllAlerts ? lowStockItems : lowStockItems.slice(0, 5)).map((item, idx) => (
                    <tr key={idx} className="border-b border-red-100 hover:bg-red-50/50" data-testid={`low-stock-row-${idx}`}>
                      <td className="py-2 px-3 font-mono font-semibold">{item.design_code}</td>
                      <td className="py-2 px-3 text-gray-600">{item.brand}</td>
                      <td className="py-2 px-3">{item.size}</td>
                      <td className="py-2 px-3">{item.color}</td>
                      <td className="py-2 px-3 text-right">
                        <span className={`font-bold ${item.current_stock === 0 ? 'text-red-600' : 'text-amber-600'}`}>{item.current_stock}</span>
                      </td>
                      <td className="py-2 px-3 text-right text-gray-500">{item.min_stock_level}</td>
                      <td className="py-2 px-3 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">-{item.deficit}</span>
                      </td>
                      <td className="py-2 px-3 text-gray-500 text-xs">{item.warehouse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!showAllAlerts && lowStockItems.length > 5 && (
              <p className="text-xs text-red-500 mt-2 text-center">Showing 5 of {lowStockItems.length} alerts</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stock Value Card */}
        <Card className="lg:col-span-1" data-testid="stock-value-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
              Total Inventory Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">
                {formatCurrency(stats.total_stock_value)}
              </div>
              <p className="text-sm text-gray-600">
                Current wholesale value
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Top Brands */}
        <Card className="lg:col-span-2" data-testid="top-brands-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <span className="text-xl">🏆</span>
              Top Performing Brands
            </CardTitle>
            <CardDescription>
              Brand performance overview
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats.top_brands.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Brand Data Yet</h3>
                <p className="text-gray-600">Start adding brands and products to see performance analytics.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {stats.top_brands.map((brand, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{brand.name}</p>
                        <p className="text-sm text-gray-600">{brand.products} products</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(brand.value)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card data-testid="recent-activities">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <span className="text-xl">📈</span>
            Recent Activities
          </CardTitle>
          <CardDescription>
            Latest updates in your ERP system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.recent_activities.map((activity, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">📋</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.message}</p>
                  <p className="text-sm text-gray-500">{formatDate(activity.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card data-testid="quick-actions">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <span className="text-xl">⚡</span>
            Quick Actions
          </CardTitle>
          <CardDescription>
            Common tasks and shortcuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors text-center group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">➕</div>
              <div className="text-sm font-medium text-blue-700">Add Product</div>
            </button>
            <button className="p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors text-center group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🏷️</div>
              <div className="text-sm font-medium text-green-700">New Brand</div>
            </button>
            <button className="p-4 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 transition-colors text-center group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📊</div>
              <div className="text-sm font-medium text-amber-700">View Reports</div>
            </button>
            <button className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors text-center group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">⚙️</div>
              <div className="text-sm font-medium text-purple-700">Settings</div>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;